import { useEffect, useState, useRef } from "react";
import { Plus, Loader2, Pencil, Trash2, GripVertical, ChevronDown, ChevronRight, X, Image as ImageIcon } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { imgUrl } from "@/lib/format";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [order, setOrder] = useState(0);
  const [cover, setCover] = useState("");
  const [uploadingCover, setUploadingCover] = useState(false);
  const coverInputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [subInputs, setSubInputs] = useState({});
  const [childSel, setChildSel] = useState({});
  const [childInputs, setChildInputs] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/categories");
      setCategories(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setName(""); setOrder(categories.length); setCover(""); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setName(c.name); setOrder(c.order); setCover(c.image_url || ""); setOpen(true); };

  const uploadCover = async (file) => {
    if (!file) return;
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCover(data.url);
      toast.success("Kapak görseli yüklendi");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Yükleme başarısız");
    } finally {
      setUploadingCover(false);
    }
  };

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/categories/${editing.id}`, { name, order: Number(order), image_url: cover });
        toast.success("Kategori güncellendi");
      } else {
        await api.post("/admin/categories", { name, order: Number(order), image_url: cover });
        toast.success("Kategori eklendi");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/categories/${delTarget.id}`);
      toast.success("Kategori silindi");
      setDelTarget(null);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const addSub = async (cat) => {
    const val = (subInputs[cat.id] || "").trim();
    if (!val) return;
    try {
      const { data } = await api.post(`/admin/categories/${cat.id}/subcategories`, { name: val });
      setCategories((cs) => cs.map((c) => (c.id === cat.id ? data : c)));
      setSubInputs((s) => ({ ...s, [cat.id]: "" }));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const removeSub = async (cat, sub) => {
    try {
      const { data } = await api.post(`/admin/categories/${cat.id}/subcategories/delete`, { name: sub });
      setCategories((cs) => cs.map((c) => (c.id === cat.id ? data : c)));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  // ---- Sub-sub categories (3rd level) ----
  const setChildren = async (cat, sub, children) => {
    try {
      const { data } = await api.post(`/admin/categories/${cat.id}/subchildren`, { subcategory: sub, children });
      setCategories((cs) => cs.map((c) => (c.id === cat.id ? data : c)));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      load();
    }
  };
  const addChild = (cat, sub) => {
    const key = `${cat.id}::${sub}`;
    const val = (childInputs[key] || "").trim();
    if (!val) return;
    const cur = (cat.sub_children || {})[sub] || [];
    if (cur.includes(val)) { toast.error("Bu alt-alt kategori zaten var"); return; }
    setChildInputs((s) => ({ ...s, [key]: "" }));
    setChildren(cat, sub, [...cur, val]);
  };
  const removeChild = (cat, sub, child) => {
    const cur = (cat.sub_children || {})[sub] || [];
    setChildren(cat, sub, cur.filter((x) => x !== child));
  };

  const inputCls = "bg-obsidian border-border text-foreground focus-visible:ring-gold";

  // ---- Drag & drop: categories ----
  const dragCat = useRef(null);
  const [dragOverCat, setDragOverCat] = useState(null);
  const onCatDrop = async (toIdx) => {
    if (dragSub.current) return;
    const from = dragCat.current;
    dragCat.current = null;
    setDragOverCat(null);
    if (from == null || from === toIdx) return;
    const next = [...categories];
    const [moved] = next.splice(from, 1);
    next.splice(toIdx, 0, moved);
    setCategories(next.map((c, i) => ({ ...c, order: i })));
    try {
      await api.post("/admin/categories/reorder", { ids: next.map((c) => c.id) });
      toast.success("Kategori sırası kaydedildi");
    } catch (err) {
      toast.error("Sıra kaydedilemedi");
      load();
    }
  };

  // ---- Drag & drop: subcategories (within a category) ----
  const dragSub = useRef(null);
  const [dragOverSub, setDragOverSub] = useState(null);
  const onSubDrop = async (cat, toIdx) => {
    const d = dragSub.current;
    dragSub.current = null;
    setDragOverSub(null);
    if (!d || d.catId !== cat.id || d.idx === toIdx) return;
    const subs = [...(cat.subcategories || [])];
    const [moved] = subs.splice(d.idx, 1);
    subs.splice(toIdx, 0, moved);
    setCategories((cs) => cs.map((c) => (c.id === cat.id ? { ...c, subcategories: subs } : c)));
    try {
      await api.post(`/admin/categories/${cat.id}/subcategories/reorder`, { subcategories: subs });
    } catch (err) {
      toast.error("Alt kategori sırası kaydedilemedi");
      load();
    }
  };

  // ---- Drag & drop: sub-sub categories ----
  const dragChild = useRef(null);
  const [dragOverChild, setDragOverChild] = useState(null);
  const onChildDrop = async (cat, sub, toIdx) => {
    const d = dragChild.current;
    dragChild.current = null;
    setDragOverChild(null);
    if (!d || d.catId !== cat.id || d.sub !== sub || d.idx === toIdx) return;
    const cur = [...((cat.sub_children || {})[sub] || [])];
    const [moved] = cur.splice(d.idx, 1);
    cur.splice(toIdx, 0, moved);
    setChildren(cat, sub, cur);
  };


  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl text-foreground">Kategoriler</h1>
          <p className="text-sm text-muted-foreground mt-1">{categories.length} kategori · sürükleyerek sıralayın · alt kategoriler için satırı açın</p>
        </div>
        <button data-testid="add-category-button" onClick={openNew} className="flex items-center gap-2 px-4 h-10 bg-gold text-obsidian font-semibold rounded-md hover:bg-gold-hover transition-colors">
          <Plus size={18} /> Kategori Ekle
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-gold" size={28} /></div>
      ) : (
        <div className="space-y-2 max-w-2xl">
          {categories.map((c, ci) => {
            const isOpen = expanded === c.id;
            const subs = c.subcategories || [];
            return (
              <div
                key={c.id}
                data-testid={`category-item-${c.name}`}
                draggable
                onDragStart={() => { dragCat.current = ci; }}
                onDragOver={(e) => { e.preventDefault(); setDragOverCat(ci); }}
                onDragEnd={() => { dragCat.current = null; setDragOverCat(null); }}
                onDrop={() => onCatDrop(ci)}
                className={`bg-surface border rounded-md transition-colors ${dragOverCat === ci ? "border-gold" : "border-border"}`}
              >
                <div className="flex items-center gap-3 px-4 py-3">
                  <button data-testid={`toggle-category-${c.name}`} onClick={() => setExpanded(isOpen ? null : c.id)} className="text-muted-foreground hover:text-gold transition-colors">
                    {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>
                  <GripVertical size={16} className="text-muted-foreground cursor-grab active:cursor-grabbing" data-testid={`drag-category-${c.name}`} />
                  <div className="w-10 h-7 rounded overflow-hidden border border-border bg-obsidian shrink-0 flex items-center justify-center">
                    {c.image_url ? <img src={imgUrl(c.image_url)} alt="" className="w-full h-full object-cover" data-testid={`category-cover-thumb-${c.name}`} /> : <ImageIcon size={13} className="text-muted-foreground/50" />}
                  </div>
                  <span className="text-xs text-muted-foreground font-mono w-6">{c.order}</span>
                  <span className="flex-1 text-foreground">{c.name}</span>
                  {subs.length > 0 && <span className="text-[10px] text-gold bg-gold/10 border border-gold/30 rounded-full px-2 py-0.5">{subs.length} alt</span>}
                  <button data-testid={`edit-category-${c.name}`} onClick={() => openEdit(c)} className="text-muted-foreground hover:text-gold transition-colors p-1"><Pencil size={16} /></button>
                  <button data-testid={`delete-category-${c.name}`} onClick={() => setDelTarget(c)} className="text-muted-foreground hover:text-red-400 transition-colors p-1"><Trash2 size={16} /></button>
                </div>

                {isOpen && (
                  <div className="px-4 pb-4 pt-1 border-t border-border" data-testid={`subcategory-panel-${c.name}`}>
                    <div className="flex flex-wrap gap-2 mb-3 mt-3">
                      {subs.length === 0 && <span className="text-xs text-muted-foreground">Henüz alt kategori yok.</span>}
                      {subs.map((s, si) => {
                        const childCount = ((c.sub_children || {})[s] || []).length;
                        const active = childSel[c.id] === s;
                        return (
                        <span
                          key={s}
                          data-testid={`subcategory-chip-${c.name}-${s}`}
                          draggable
                          onDragStart={(e) => { e.stopPropagation(); dragSub.current = { catId: c.id, idx: si }; }}
                          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverSub(`${c.id}-${si}`); }}
                          onDragEnd={() => { dragSub.current = null; setDragOverSub(null); }}
                          onDrop={(e) => { e.stopPropagation(); onSubDrop(c, si); }}
                          className={`flex items-center gap-1.5 bg-elevated border rounded-full pl-2 pr-2 py-1 text-xs text-foreground cursor-grab active:cursor-grabbing transition-colors ${active ? "border-gold ring-1 ring-gold/40" : dragOverSub === `${c.id}-${si}` ? "border-gold" : "border-border"}`}
                        >
                          <GripVertical size={12} className="text-muted-foreground" />
                          <button type="button" data-testid={`subcat-select-${c.name}-${s}`} onClick={(e) => { e.stopPropagation(); setChildSel((cs) => ({ ...cs, [c.id]: cs[c.id] === s ? undefined : s })); }} className="hover:text-gold transition-colors flex items-center gap-1">
                            {s}
                            {childCount > 0 && <span className="text-[9px] text-gold bg-gold/15 rounded-full px-1.5">{childCount}</span>}
                          </button>
                          <button data-testid={`delete-subcategory-${c.name}-${s}`} onClick={() => removeSub(c, s)} className="text-muted-foreground hover:text-red-400 transition-colors"><X size={13} /></button>
                        </span>
                        );
                      })}
                    </div>

                    {childSel[c.id] && subs.includes(childSel[c.id]) && (() => {
                      const sub = childSel[c.id];
                      const children = (c.sub_children || {})[sub] || [];
                      const key = `${c.id}::${sub}`;
                      return (
                        <div className="mb-3 rounded-md border border-gold/20 bg-obsidian/40 p-3" data-testid={`subchildren-panel-${c.name}-${sub}`}>
                          <p className="text-[11px] text-muted-foreground mb-2"><b className="text-gold">{sub}</b> alt-alt kategorileri · sürükleyerek sıralayın</p>
                          <div className="flex flex-wrap gap-2 mb-2">
                            {children.length === 0 && <span className="text-xs text-muted-foreground">Henüz yok.</span>}
                            {children.map((ch, ci) => (
                              <span key={ch} data-testid={`subchild-chip-${c.name}-${sub}-${ch}`} draggable
                                onDragStart={(e) => { e.stopPropagation(); dragChild.current = { catId: c.id, sub, idx: ci }; }}
                                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverChild(`${key}-${ci}`); }}
                                onDragEnd={() => { dragChild.current = null; setDragOverChild(null); }}
                                onDrop={(e) => { e.stopPropagation(); onChildDrop(c, sub, ci); }}
                                className={`flex items-center gap-1 bg-elevated border rounded-full pl-2 pr-2 py-1 text-xs cursor-grab active:cursor-grabbing ${dragOverChild === `${key}-${ci}` ? "border-gold" : "border-border"}`}>
                                <GripVertical size={11} className="text-muted-foreground" />
                                {ch}
                                <button data-testid={`delete-subchild-${c.name}-${sub}-${ch}`} onClick={() => removeChild(c, sub, ch)} className="text-muted-foreground hover:text-red-400 transition-colors"><X size={12} /></button>
                              </span>
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <Input data-testid={`subchild-input-${c.name}-${sub}`} value={childInputs[key] || ""} onChange={(e) => setChildInputs((st) => ({ ...st, [key]: e.target.value }))} onKeyDown={(e) => { if (e.key === "Enter") addChild(c, sub); }} placeholder="Örn. Kalemli, Tek Renk, 3 Renk" className={`${inputCls} h-8 text-xs`} />
                            <button data-testid={`add-subchild-${c.name}-${sub}`} onClick={() => addChild(c, sub)} className="shrink-0 px-3 h-8 border border-border rounded-md text-xs hover:border-gold transition-colors flex items-center gap-1"><Plus size={13} /> Ekle</button>
                          </div>
                        </div>
                      );
                    })()}
                    <div className="flex gap-2">
                      <Input
                        data-testid={`subcategory-input-${c.name}`}
                        value={subInputs[c.id] || ""}
                        onChange={(e) => setSubInputs((si) => ({ ...si, [c.id]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === "Enter") addSub(c); }}
                        placeholder="Örn. Baget, Tektaş, Kalemli"
                        className={`${inputCls} h-9`}
                      />
                      <button data-testid={`add-subcategory-${c.name}`} onClick={() => addSub(c)} className="shrink-0 px-3 h-9 border border-border rounded-md text-sm hover:border-gold transition-colors flex items-center gap-1">
                        <Plus size={15} /> Ekle
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {categories.length === 0 && <p className="text-center text-muted-foreground py-8">Kategori bulunamadı.</p>}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-surface border-border text-foreground max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Kategori Düzenle" : "Yeni Kategori"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] text-muted-foreground mb-1 block">Kategori Adı</label>
              <Input data-testid="category-name-input" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground mb-1 block">Sıralama</label>
              <Input data-testid="category-order-input" type="number" value={order} onChange={(e) => setOrder(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground mb-1 block">Kapak Görseli</label>
              <input type="file" ref={coverInputRef} accept="image/*" className="hidden" data-testid="category-cover-input" onChange={(e) => uploadCover(e.target.files?.[0])} />
              <div className="flex items-center gap-3">
                <div className="w-24 h-16 rounded-md overflow-hidden border border-border bg-obsidian flex items-center justify-center shrink-0">
                  {cover ? <img src={imgUrl(cover)} alt="" className="w-full h-full object-cover" data-testid="category-cover-preview" /> : <ImageIcon size={20} className="text-muted-foreground/50" />}
                </div>
                <div className="flex flex-col gap-2">
                  <button type="button" data-testid="category-cover-upload-button" onClick={() => coverInputRef.current?.click()} disabled={uploadingCover} className="px-3 h-9 border border-border rounded-md text-sm hover:border-gold transition-colors flex items-center gap-2 disabled:opacity-60">
                    {uploadingCover ? <Loader2 className="animate-spin" size={15} /> : <ImageIcon size={15} />} Görsel Seç
                  </button>
                  {cover && <button type="button" data-testid="category-cover-remove-button" onClick={() => setCover("")} className="text-xs text-muted-foreground hover:text-red-400 transition-colors text-left">Kaldır</button>}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 h-10 border border-border rounded-md text-muted-foreground hover:text-foreground transition-colors">İptal</button>
            <button data-testid="save-category-button" onClick={save} disabled={saving} className="px-5 h-10 bg-gold text-obsidian font-semibold rounded-md hover:bg-gold-hover transition-colors flex items-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="animate-spin" size={16} />} Kaydet
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent className="bg-surface border-border text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle>Kategoriyi sil?</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">{delTarget?.name} silinecek. Bu kategorideki ürünler etkilenmez.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-border text-foreground hover:bg-elevated">Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-category" onClick={doDelete} className="bg-red-500 text-white hover:bg-red-600">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
